import { getCurrentUser, getAuthHeader, API_BASE_URL, post, get } from './base';
import toast from "react-hot-toast";

export const surveyChat = async ({ surveyId, messages, conversationId = null, memoryEnabled = true }) => {
  return post(`/ai/survey-chat`, { surveyId, messages, conversationId, memoryEnabled });
};

// AI memory (personalization) — what the assistant remembers about this user.
export const getMemory = async () => {
  return get(`/memory`);
};

export const updateMemory = async ({ summary, enabled } = {}) => {
  const body = {};
  if (summary !== undefined) body.summary = summary;
  if (enabled !== undefined) body.enabled = enabled;
  return post(`/memory/update`, body);
};

export const attachConversationToSurvey = async (conversationId, surveyId) => {
  return post(`/conversations/${conversationId}/attach-survey`, { surveyId });
};

export const getConversationsBySurvey = async (surveyId) => {
  return get(`/conversations?surveyId=${surveyId}`);
};

export const getConversation = async (conversationId) => {
  return get(`/conversations/${conversationId}`);
};

export const createConversation = async (surveyId) => {
  return post(`/conversations`, { surveyId });
};

export const appendConversationMessage = async (conversationId, role, content, metadata) => {
  return post(`/conversations/${conversationId}/messages`, { role, content, metadata });
};

export const generateAiSuggestions = async (surveyData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(
      `${API_BASE_URL}/api/ai/generate-ai-suggestion`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          description: surveyData.description,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error generating ai suggestions:", error);
    throw error;
  }
};

export const generateAiQuestions = async (surveyData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const requestBody = {
      description: surveyData.description,
    };

    // Add temporary imageIds if provided
    if (surveyData.imageIds && surveyData.imageIds.length > 0) {
      requestBody.imageIds = surveyData.imageIds;
    }

    // Add library images (with URLs) if provided
    if (surveyData.libraryImages && surveyData.libraryImages.length > 0) {
      requestBody.libraryImages = surveyData.libraryImages;
    }

    const response = await fetch(
      `${API_BASE_URL}/api/ai/generate-ai-questions`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    toast.error("Error while generating with AI.");
    console.error("Error generating ai questions:", error);
    throw error;
  }
};

export const formBuilderAgent = async ({ messages, brandkitContext, confirmedBrand, conversationId, files, onToken, onReasoning, onStatus, onToolUse, signal }) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");

    const headers = await getAuthHeader();

    const formData = new FormData();
    formData.append("messages", JSON.stringify(messages));
    if (brandkitContext) {
      formData.append("brandkitContext", JSON.stringify(brandkitContext));
    }
    if (confirmedBrand) {
      formData.append("confirmedBrand", JSON.stringify(confirmedBrand));
    }
    if (conversationId) {
      formData.append("conversationId", conversationId);
    }
    if (files && files.length > 0) {
      files.forEach((file) => formData.append("files", file));
    }

    const response = await fetch(
      `${API_BASE_URL}/api/ai/form-builder-agent`,
      {
        method: "POST",
        headers,
        body: formData,
        signal,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Agent response error:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalData = null;
    let receivedReasoningStream = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6).trim();
        if (payload === "[DONE]") continue;

        try {
          const event = JSON.parse(payload);
          if (event.type === "token") {
            if (onToken) onToken(event.content);
          } else if (event.type === "reasoning") {
            if (onReasoning) onReasoning(event.content);
            if (event.content && String(event.content).trim()) {
              receivedReasoningStream = true;
            }
          } else if (event.type === "status") {
            if (onStatus) onStatus(event);
          } else if (event.type === "tool_use") {
            if (onToolUse) onToolUse(event);
          } else if (event.type === "done") {
            finalData = event.data;
          } else if (event.type === "error") {
            throw new Error(event.error || "Agent stream error");
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            console.warn("[formBuilderAgent] Bad SSE line (non-JSON):", payload.slice(0, 120));
            continue;
          }
          throw e;
        }
      }
    }

    if (!finalData) {
      throw new Error("Failed to get agent response");
    }

    // Gateway sometimes omits incremental reasoning events; apply final payload once so "Thinking" can show bullets.
    if (
      onReasoning &&
      typeof finalData.reasoning === "string" &&
      finalData.reasoning.trim() &&
      !receivedReasoningStream
    ) {
      onReasoning(finalData.reasoning);
    }

    return finalData;
  } catch (error) {
    console.error("Error in form builder agent:", error);
    throw error;
  }
};

function extractUntilUnescapedQuote(str) {
  let text = "";
  let i = 0;
  while (i < str.length) {
    if (str[i] === "\\" && i + 1 < str.length) {
      text += str[i] + str[i + 1];
      i += 2;
    } else if (str[i] === '"') {
      return { text, closed: true };
    } else {
      text += str[i];
      i++;
    }
  }
  return { text, closed: false };
}

function unescapeJson(str) {
  return str.replace(/\\n/g, "\n").replace(/\\t/g, "\t").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
}

export const formBuilderAgentVariants = async ({ messages, brandkitContext, confirmedBrand }) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error("No authenticated user");
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(
      `${API_BASE_URL}/api/ai/form-builder-agent/variants`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ messages, brandkitContext, confirmedBrand }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating variants:", error);
    throw error;
  }
};

/**
 * Generate AI image for survey (async, non-blocking)
 * Called separately after survey creation to avoid timeouts
 * @param {Object} params - { surveyId, title, description, styles }
 * @returns {Promise<{success: boolean, imageUrl?: string, error?: string}>}
 */
export const generateSurveyImage = async ({ surveyId, title, description, styles }) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(
      `${API_BASE_URL}/api/ai/generate-survey-image`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          surveyId,
          title,
          description,
          styles
        }),
      }
    );

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("[generateSurveyImage] Error:", error);
    // Don't show toast - this is a background operation
    return { success: false, error: error.message };
  }
};

export const importAiQuestions = async (surveyData) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/import-ai-questions`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        description: surveyData.description,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    toast.error("Error while importing.");
    console.error("Error importing questions:", error);
    throw error;
  }
};

export const modifySurveyDesign = async (surveyId, prompt, currentStyles, welcomeScreen = null, endingScreens = null, currentQuestions = null, options = {}) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/modify-survey-design`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        surveyId,
        prompt,
        currentStyles,
        welcomeScreen,
        endingScreens,
        currentQuestions,
        imageIds: options?.imageIds || undefined,
        libraryImages: options?.libraryImages || undefined,
        conversationId: options?.conversationId || undefined,
        activeQuestionIndex: options?.activeQuestionIndex ?? undefined,
        activeScreen: options?.activeScreen ?? undefined,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error modifying survey design:", error);
    throw error;
  }
};

export const translateSurvey = async (surveyId, targetLanguage) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/translate-survey`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        surveyId,
        targetLanguage,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error translating survey:", error);
    throw error;
  }
};

export const addQuestionsToSurvey = async (surveyId, prompt, currentQuestions, surveyTitle, options = {}) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/add-questions`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        surveyId,
        prompt,
        currentQuestions,
        surveyTitle,
        ...(options.conversationId ? { conversationId: options.conversationId } : {}),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding questions:", error);
    throw error;
  }
};

export const generateCustomImage = async ({ surveyId, prompt, placement, questionIndex }) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/generate-custom-image`, {
      method: "POST",
      headers,
      body: JSON.stringify({ surveyId, prompt, placement, questionIndex }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating custom image:", error);
    throw error;
  }
};

export const addImagesToSurvey = async (surveyId, currentQuestions, welcomeScreen, specificQuestionIndex = null, onlyWelcomeScreen = false) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/ai/add-images`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        surveyId,
        currentQuestions,
        welcomeScreen,
        specificQuestionIndex,
        onlyWelcomeScreen,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding images:", error);
    throw error;
  }
};
